import { useState } from 'react'
import { Camera } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import { extractImage, ingestExtracted } from '~/api/monitoring'
import type { ExtractedRow, MonitoringIndicator } from '~/api/types'
import { errMessage } from '~/lib/errors'
import { Dropzone, fileToScaledBase64 } from './ingest-helpers'

/** 일지 사진(VLM) 판독 → 일자별 측정값 추출 후 확인 적재. */
export function ImageIngestPanel({
    dashboardId,
    indicators,
    onIngested,
}: {
    dashboardId: string
    indicators: MonitoringIndicator[]
    onIngested?: () => void
}) {
    const [imgBusy, setImgBusy] = useState(false)
    const [imgProgress, setImgProgress] = useState('')
    const [extractedRows, setExtractedRows] = useState<ExtractedRow[] | null>(
        null
    )
    const [filename, setFilename] = useState<string | null>(null)
    const [extractNote, setExtractNote] = useState('')
    const [busy, setBusy] = useState(false)
    const [msg, setMsg] = useState<string | null>(null)
    const [err, setErr] = useState<string | null>(null)

    const indicatorLabel = (fieldId: string) => {
        const ind = indicators.find((i) => i.fieldId === fieldId)
        return ind
            ? `${ind.nameKo}${ind.unit ? ` (${ind.unit})` : ''}`
            : fieldId
    }

    const onImages = async (files: File[]) => {
        setErr(null)
        setMsg(null)
        setExtractedRows(null)
        setFilename(
            files.length === 1
                ? files[0].name
                : `${files[0].name} 외 ${files.length - 1}장`
        )
        setImgBusy(true)
        try {
            const all: ExtractedRow[] = []
            let note = ''
            for (let i = 0; i < files.length; i++) {
                setImgProgress(
                    files.length > 1
                        ? `${i + 1}/${files.length}장 판독 중…`
                        : ''
                )
                const { base64, mime } = await fileToScaledBase64(files[i])
                const res = await extractImage(dashboardId, {
                    imageBase64: base64,
                    mime,
                })
                all.push(...res.rows)
                if (res.note) note = res.note
            }
            setExtractedRows(all)
            setExtractNote(note)
            if (all.length === 0) {
                setMsg('이미지에서 읽을 수 있는 측정값을 찾지 못했어요.')
            } else if (files.length > 1) {
                setMsg(
                    `${files.length}장에서 ${all.length}일자를 읽었어요. 중복 시각은 적재 시 최신 값으로 합쳐집니다.`
                )
            }
        } catch (e: unknown) {
            setErr(errMessage(e, '이미지 판독에 실패했어요.'))
        } finally {
            setImgBusy(false)
            setImgProgress('')
        }
    }

    const confirmExtracted = async () => {
        if (!extractedRows || extractedRows.length === 0) return
        setBusy(true)
        setErr(null)
        try {
            const res = await ingestExtracted(dashboardId, {
                rows: extractedRows,
                filename,
            })
            setMsg(
                `이미지에서 읽은 측정값 ${res.inserted}건을 적재했어요. '데이터베이스' 탭에서 확인하세요.`
            )
            setExtractedRows(null)
            onIngested?.()
        } catch (e: unknown) {
            setErr(errMessage(e, '적재에 실패했어요.'))
        } finally {
            setBusy(false)
        }
    }

    return (
        <>
            <section className='border-border bg-card flex flex-col rounded-md border p-5'>
                <div className='mb-3 flex items-start gap-3'>
                    <div className='bg-accent text-primary grid size-10 shrink-0 place-items-center rounded-md'>
                        <Camera className='size-5' />
                    </div>
                    <div>
                        <div className='flex items-center gap-2'>
                            <h3 className='text-foreground text-sm font-semibold'>
                                일지 사진
                            </h3>
                            <Badge tone='info'>AI 판독</Badge>
                        </div>
                        <p className='text-muted-foreground mt-0.5 text-xs'>
                            종이 일지를 찍어 올리면 일자별로 읽어요.
                        </p>
                    </div>
                </div>
                <Dropzone
                    accept='image/*'
                    multiple
                    onFile={(f) => void onImages([f])}
                    onFiles={(fs) => void onImages(fs)}
                    busy={imgBusy}
                    icon={<Camera className='size-5' />}
                    title={
                        imgBusy
                            ? imgProgress || '판독 중…'
                            : '사진 여러 장 끌어다 놓기 또는 클릭'
                    }
                    hint='JPG · PNG · 여러 장 가능 · 또렷하게 촬영'
                />
            </section>

            {extractedRows ? (
                <section className='border-border bg-card rounded-md border p-5'>
                    <p className='text-foreground mb-2 text-sm font-medium'>
                        사진 판독 결과 미리보기 ({extractedRows.length}일자)
                    </p>
                    {extractNote ? (
                        <p className='text-muted-foreground mb-2 text-xs'>
                            참고: {extractNote}
                        </p>
                    ) : null}
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='text-muted-foreground border-border border-b text-xs'>
                                <th className='py-2 pr-3 text-left'>
                                    측정 시각
                                </th>
                                <th className='py-2 text-left'>읽은 값</th>
                            </tr>
                        </thead>
                        <tbody>
                            {extractedRows.slice(0, 30).map((row, idx) => (
                                <tr
                                    key={`${row.measuredAt}-${idx}`}
                                    className='border-border border-b last:border-0'>
                                    <td className='text-muted-foreground py-2 pr-3 text-xs'>
                                        {row.measuredAt}
                                    </td>
                                    <td className='py-2'>
                                        {row.readings
                                            .map(
                                                (r) =>
                                                    `${indicatorLabel(r.fieldId)}: ${r.value}`
                                            )
                                            .join(' · ')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {extractedRows.length > 0 ? (
                        <Button
                            onClick={confirmExtracted}
                            loading={busy}
                            className='mt-3'>
                            이 내용으로 적재
                        </Button>
                    ) : null}
                </section>
            ) : null}

            {msg ? (
                <ErrorBanner tone='success' className='bg-accent text-primary'>
                    {msg}
                </ErrorBanner>
            ) : null}
            {err ? (
                <ErrorBanner className='bg-point-subtle text-point'>
                    {err}
                </ErrorBanner>
            ) : null}
        </>
    )
}
